/**
 * HostPoint POS - Offline Order Service
 * Handles creating, storing, and syncing orders when offline
 */

import offlineDB from './offline-storage';

const OFFLINE_ORDER_PREFIX = 'OFFLINE';

/**
 * Generate a unique offline order number
 */
function generateOfflineOrderNumber() {
  const timestamp = Date.now().toString(36).toUpperCase();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `${OFFLINE_ORDER_PREFIX}-${timestamp}-${random}`;
}

/**
 * Create an offline order when network is unavailable
 */
export async function createOfflineOrder(orderData, user, outlet) {
  const offlineOrderNumber = generateOfflineOrderNumber();
  const now = new Date().toISOString();
  
  const offlineOrder = {
    id: `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    order_number: offlineOrderNumber,
    is_offline: true,
    synced: false,
    sync_attempts: 0,
    last_sync_attempt: null,
    sync_error: null,
    created_at: now,
    
    // Order data
    items: orderData.items.map(item => ({
      product_id: item.id,
      name: item.name,
      quantity: item.quantity,
      price: item.price,
      total: item.price * item.quantity,
      notes: item.notes || '',
      category: item.category || ''
    })),
    
    subtotal: orderData.subtotal,
    tax: orderData.tax,
    discount: orderData.discount || 0,
    total: orderData.total,
    
    // Customer info
    customer_name: orderData.customerName || 'Walk-in',
    customer_phone: orderData.customerPhone || '',
    customer_id: orderData.customerId || null,
    
    order_type: orderData.orderType || 'retail',
    
    // Payment info
    payment_method: orderData.paymentMethod || 'cash',
    payment_status: orderData.paymentMethod === 'cash' ? 'paid' : 'pending',
    amount_paid: orderData.amountPaid || orderData.total,
    change_given: orderData.change || 0,
    
    // User/Outlet context
    user_id: user?.id,
    user_name: user?.full_name || user?.username,
    outlet_id: outlet?.id,
    outlet_name: outlet?.name,
    property_id: outlet?.property_id,
    
    // Token for syncing
    auth_token: localStorage.getItem('auth_token'),
    
    // Receipt data for printing
    receipt_data: {
      businessName: outlet?.property_name || 'HostPoint POS',
      address: outlet?.address || '',
      phone: outlet?.phone || '',
      receiptNumber: offlineOrderNumber,
      date: new Date().toLocaleString(),
      tableNumber: orderData.tableNumber,
      customerName: orderData.customerName,
      servedBy: user?.full_name || user?.username,
      items: orderData.items,
      subtotal: orderData.subtotal,
      tax: orderData.tax,
      discount: orderData.discount,
      total: orderData.total,
      paymentMethod: orderData.paymentMethod,
      footer: 'Thank you! (Offline Order)'
    }
  };
  
  // Save to IndexedDB
  await offlineDB.saveItem('pending-orders', offlineOrder);
  
  // Add to sync queue
  await offlineDB.addToSyncQueue('order', offlineOrder.id, {
    type: 'create_order',
    order_data: offlineOrder
  });
  
  
  return offlineOrder;
}

/**
 * Get all pending offline orders
 */
export async function getPendingOfflineOrders() {
  const orders = await offlineDB.getAllItems('pending-orders');
  return orders.filter(o => !o.synced).sort((a, b) => 
    new Date(b.created_at) - new Date(a.created_at)
  );
}

/**
 * Get synced offline orders (for history)
 */
export async function getSyncedOfflineOrders() {
  const orders = await offlineDB.getAllItems('pending-orders');
  return orders.filter(o => o.synced).sort((a, b) => 
    new Date(b.created_at) - new Date(a.created_at)
  );
}

/**
 * Sync a single offline order to the server
 */
export async function syncOfflineOrder(offlineOrder, apiUrl) {
  const token = offlineOrder.auth_token || localStorage.getItem('auth_token');
  
  if (!token) {
    throw new Error('No authentication token available');
  }
  
  // Update sync attempt info
  offlineOrder.sync_attempts = (offlineOrder.sync_attempts || 0) + 1;
  offlineOrder.last_sync_attempt = new Date().toISOString();
  
  try {
    // Prepare order data for API
    const apiOrderData = {
      items: offlineOrder.items,
      customer_name: offlineOrder.customer_name,
      customer_phone: offlineOrder.customer_phone,
      customer_id: offlineOrder.customer_id,
      order_type: offlineOrder.order_type,
      payment_method: offlineOrder.payment_method,
      subtotal: offlineOrder.subtotal,
      tax: offlineOrder.tax,
      discount: offlineOrder.discount,
      total: offlineOrder.total,
      notes: `Offline Order: ${offlineOrder.order_number}`,
      offline_order_id: offlineOrder.id,
      offline_order_number: offlineOrder.order_number
    };
    
    const response = await fetch(`${apiUrl}/api/orders`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(apiOrderData)
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Server error: ${response.status} - ${errorText}`);
    }
    
    const serverOrder = await response.json();
    
    // Mark as synced
    offlineOrder.synced = true;
    offlineOrder.synced_at = new Date().toISOString();
    offlineOrder.server_order_id = serverOrder.id;
    offlineOrder.server_order_number = serverOrder.order_number;
    offlineOrder.sync_error = null;
    
    await offlineDB.saveItem('pending-orders', offlineOrder);
    
    
    return {
      success: true,
      offlineOrder,
      serverOrder
    };
  } catch (error) {
    
    offlineOrder.sync_error = error.message;
    await offlineDB.saveItem('pending-orders', offlineOrder);
    
    return {
      success: false,
      offlineOrder,
      error: error.message
    };
  }
}

/**
 * Sync all pending offline orders
 */
export async function syncAllOfflineOrders(apiUrl, onProgress) {
  const pendingOrders = await getPendingOfflineOrders();
  
  if (pendingOrders.length === 0) {
    return { synced: 0, failed: 0, total: 0 };
  }
  
  const results = {
    synced: 0,
    failed: 0,
    total: pendingOrders.length,
    details: []
  };
  
  for (let i = 0; i < pendingOrders.length; i++) {
    const order = pendingOrders[i];
    
    if (onProgress) {
      onProgress({
        current: i + 1,
        total: pendingOrders.length,
        order: order.order_number
      });
    }
    
    const result = await syncOfflineOrder(order, apiUrl);
    
    if (result.success) {
      results.synced++;
    } else {
      results.failed++;
    }
    
    results.details.push(result);
    
    // Small delay between syncs to avoid overwhelming server
    await new Promise(resolve => setTimeout(resolve, 500));
  }
  
  return results;
}

/**
 * Delete an offline order (before sync)
 */
export async function deleteOfflineOrder(orderId) {
  await offlineDB.deleteItem('pending-orders', orderId);
}

/**
 * Clear all synced orders older than specified days
 */
export async function clearOldSyncedOrders(daysOld = 7) {
  const orders = await offlineDB.getAllItems('pending-orders');
  const cutoffDate = new Date(Date.now() - daysOld * 24 * 60 * 60 * 1000);
  
  let deleted = 0;
  for (const order of orders) {
    if (order.synced && new Date(order.synced_at) < cutoffDate) {
      await offlineDB.deleteItem('pending-orders', order.id);
      deleted++;
    }
  }
  
  return deleted;
}

/**
 * Check if there are any pending orders to sync
 */
export async function hasPendingOrders() {
  const pending = await getPendingOfflineOrders();
  return pending.length > 0;
}

/**
 * Get sync status summary
 */
export async function getOfflineOrderStats() {
  const orders = await offlineDB.getAllItems('pending-orders');
  
  const pending = orders.filter(o => !o.synced);
  const synced = orders.filter(o => o.synced);
  const failed = pending.filter(o => o.sync_error);
  
  return {
    pending: pending.length,
    synced: synced.length,
    failed: failed.length,
    total: orders.length,
    pendingTotal: pending.reduce((sum, o) => sum + (o.total || 0), 0),
    oldestPending: pending.length > 0 ? 
      pending.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))[0].created_at : null
  };
}

export default {
  createOfflineOrder,
  getPendingOfflineOrders,
  getSyncedOfflineOrders,
  syncOfflineOrder,
  syncAllOfflineOrders,
  deleteOfflineOrder,
  clearOldSyncedOrders,
  hasPendingOrders,
  getOfflineOrderStats
};
